import type { ReactNode, VoidFunctionComponent } from 'react';
import { useEffect, useState } from 'react';
import { LabeledCheckbox } from '../../../generic';
import PropagateModal from '../../shared/SettingsPropagateModal';
import useMutation from '../../../../ReactQuery/useMutation';
import type { SBOMSettings, SBOMSettingsFields } from '../../../../../shared/settings/project/sbom';
import { patch } from '../../../../redux/api';
import { window } from '../../../../globals';
import { useAddToast } from '../../../../contexts/ToastContext';
import { errorMessagesListFromResponse } from '../../../../helpers/errors';
import { UNSET_POLICY_OPTION } from '../../../shared/policies/hooks/usePolicies';

type Props = {
  isOpen: boolean;
  onHide: () => void;
  policyName: ReactNode;
  settings: SBOMSettings;
};

/**
 * PropagateSBOMModal is the modal for propagating SBOM settings to all projects.
 */
const PropagateSBOMModal: VoidFunctionComponent<Props> = ({ isOpen, onHide, policyName, settings }) => {
  const addToast = useAddToast();
  const [isPropagateSBOMPolicyChecked, setIsPropagateSBOMPolicyChecked] = useState(false);
  const [isPropagateSBOMAnalysisEnabledChecked, setIsPropagateSBOMAnalysisEnabledChecked] = useState(false);
  const onHideAction = () => {
    setIsPropagateSBOMPolicyChecked(false);
    setIsPropagateSBOMAnalysisEnabledChecked(false);
    onHide();
  };
  const { isLoading, mutate, error, reset } = useMutation({
    mutationFn: async (req: SBOMSettingsFields[]) => {
      const organizationId = window?.user?.organizationId as number;
      await patch<SBOMSettingsFields[], void>(`/api/organizations/${organizationId}/settings/projects/sbom`, req);
      addToast({ type: 'success', content: 'Settings successfully applied to all projects.' });
      onHideAction();
    },
  });
  // Reset the state when the modal is opened
  useEffect(() => {
    if (isOpen) {
      reset();
      setIsPropagateSBOMPolicyChecked(false);
    }
  }, [isOpen, reset]);
  const policyLabel =
    policyName === UNSET_POLICY_OPTION.label
      ? 'Unset the SBOM policy in all projects'
      : `Override all projects to use SBOM policy "${policyName}"`;
  const sbomAnalysisLabel = settings.projectSBOMAnalysisEnabled
    ? 'Enable SBOM analysis in all projects'
    : 'Disable SBOM analysis in all projects';

  return (
    <PropagateModal
      settingsCategory="SBOM"
      isOpen={isOpen}
      errors={error ? errorMessagesListFromResponse([error]) : []}
      onHide={onHideAction}
      onSave={() => {
        const propagateFields: SBOMSettingsFields[] = [];
        if (isPropagateSBOMPolicyChecked) {
          propagateFields.push('defaultSBOMPolicyId');
        }
        if (isPropagateSBOMAnalysisEnabledChecked) {
          propagateFields.push('projectSBOMAnalysisEnabled');
        }
        if (propagateFields.length > 0) {
          mutate(propagateFields);
        }
      }}
      isSaveButtonDisabled={!isPropagateSBOMPolicyChecked && !isPropagateSBOMAnalysisEnabledChecked}
      isSaveButtonLoading={isLoading}
    >
      <LabeledCheckbox
        label={sbomAnalysisLabel}
        isChecked={isPropagateSBOMAnalysisEnabledChecked}
        onChange={() => {
          setIsPropagateSBOMAnalysisEnabledChecked(!isPropagateSBOMAnalysisEnabledChecked);
        }}
        isRippleDisabled
      />
      <LabeledCheckbox
        label={policyLabel}
        isChecked={isPropagateSBOMPolicyChecked}
        onChange={() => {
          setIsPropagateSBOMPolicyChecked(!isPropagateSBOMPolicyChecked);
        }}
        isRippleDisabled
      />
    </PropagateModal>
  );
};

export default PropagateSBOMModal;
